import Grid from '@mui/material/Grid2';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { SnackbarOpen } from '#app/Action/SnackbarAction';
import { dispatch } from '#app/App';
import SelectComponent from '#app/Component/SelectComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { Form, FormField, initFormHandler } from '#app/Handler/FormHandler';
import { Product } from '#app/View/ClientService/OrderClientLitige';
import Modal from '#app/View/ClientService/OrderClientLitige/components/Modal';
import { CarrierSelect } from '#app/typings/carrier';

type Address = {
  firstname?: string;
  lastname?: string;
  address1?: string;
  address2?: string;
  city?: string;
  zipCode?: string;
  countryCode?: string;
  company?: string;
  phone?: string;
};

type Props = {
  reload: () => void;
  onClose: () => void;
  products: Product[];
  currentCarrier?: number;
  addressBilling?: Address;
  addressShipping?: Address;
};

const ReshipmentModal: React.FC<Props> = ({
  onClose,
  reload,
  products,
  currentCarrier,
  addressBilling,
  addressShipping,
}) => {
  const params = useParams<{ uuid: string }>();
  const uuid = params.uuid as string;
  const [nextStep, setNextStep] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const classes = useStyles();
  const [optionsCarrier, setOptionsCarrier] = useState<CarrierSelect[]>([]);

  const [form, setForm] = useState<Form>({
    carrier: {
      name: 'carrier',
      label: 'Transporteur',
      textHelper: 'Saisissez le transporteur.',
      type: 'integer',
      options: { validation: ['required'] },
    },
    ...products.reduce((acc: Record<string, FormField>, product) => {
      acc[`products.${product.id}`] = {
        name: `products.${product.id}`,
        label: 'Quantité',
        textHelper: `Saisissez la quantité de produit en litige (maximum : ${product.available})`,
        type: 'integer',
        options: {
          validation: ['required'],
          max: product.available,
        },
      };
      return acc;
    }, {}),
  });
  const [formAddress, setFormAddress] = useState<Form>({
    lastname: {
      name: 'lastname',
      label: 'Nom',
      textHelper: 'Nom du client.',
      type: 'text',
      defaultValue: addressBilling?.lastname,
      options: { validation: ['required'] },
    },
    firstname: {
      name: 'firstname',
      label: 'Prénom',
      textHelper: 'Prénom du client.',
      type: 'text',
      defaultValue: addressBilling?.firstname,
      options: { validation: ['required'] },
    },
    address1: {
      name: 'address1',
      label: 'Adresse 1',
      textHelper: 'Adresse principale du client.',
      type: 'text',
      defaultValue: addressBilling?.address1,
      options: { validation: ['required'] },
    },
    address2: {
      name: 'address2',
      label: 'Adresse 2',
      textHelper: 'Adresse secondaire du client.',
      type: 'text',
      defaultValue: addressBilling?.address2,
    },
    city: {
      name: 'city',
      label: 'Ville',
      type: 'text',
      defaultValue: addressBilling?.city,
      options: { validation: ['required'] },
    },
    zipCode: {
      name: 'zipCode',
      label: 'Code postal',
      type: 'text',
      defaultValue: addressBilling?.zipCode,
      options: { validation: ['required'] },
    },
    countryCode: {
      name: 'countryCode',
      label: 'Code Pays',
      textHelper: 'Référence du pays',
      type: 'text',
      defaultValue: addressBilling?.countryCode,
      options: { validation: ['required'] },
    },
    company: {
      name: 'company',
      label: 'Entreprise',
      textHelper: 'Entreprise à livrer',
      type: 'text',
      defaultValue: addressBilling?.company,
    },
    phone: {
      name: 'phone',
      label: 'Téléphone',
      textHelper: 'Téléphone du client',
      type: 'text',
      defaultValue: addressBilling?.phone ?? addressShipping?.phone,
      options: { validation: ['required'] },
    },
  });
  const handler = initFormHandler(form, setForm);
  const handlerAddress = initFormHandler(formAddress, setFormAddress);

  const handleSubmit = () => {
    if (handlerAddress.checkError() > 0) {
      return;
    }
    const data = handler.getData();
    const products = Object.entries(data)
      .filter(([key]) => key.startsWith('products.'))
      .map(([key, value]) => ({
        quantity: value ?? 0,
        byProduct: parseInt(key.split('.')[1], 10),
      }));

    if (!products.some((product) => product.quantity > 0)) {
      dispatch(SnackbarOpen({ text: 'Il faut au moins un produit', variant: 'error' }));

      return;
    }

    setLoading(true);
    ApiHandler.post(
      {
        route: 'api_client_service_order_client_litige_reshipment',
        params: { uuid },
        data: {
          products,
          carrier: data.carrier,
          reshipmentAddress: handlerAddress.getData(),
        },
      },
      (response) => {
        if (response.status === 200) {
          dispatch(
            SnackbarOpen({
              text: 'Demande de réexpédition créée avec succès.',
              variant: 'success',
            }),
          );
          reload();
        } else {
          dispatch(
            SnackbarOpen({
              text: response?.error?.message || "Une erreur inattendue s'est produite.",
              variant: 'error',
            }),
          );
        }
        setLoading(false);
        onClose();
      },
    );
  };

  useEffect(() => {
    handler.start();
    handlerAddress.start();

    ApiHandler.get<CarrierSelect[]>({ route: 'api_select_carrier_dpd' }, (response) => {
      setOptionsCarrier(response.data ?? []);
      if (currentCarrier && response.data?.some((item) => item.value === currentCarrier)) {
        handler.setValue('carrier', currentCarrier);
      } else if (response.data?.length === 1) {
        handler.setValue('carrier', response.data[0].value);
      }
    });
  }, []);

  return (
    <Modal
      title="Réexpédition"
      onClose={!nextStep ? onClose : () => setNextStep(false)}
      onSubmit={!nextStep ? () => handler.checkError() === 0 && setNextStep(true) : handleSubmit}
      loading={loading}
    >
      {!nextStep ? (
        <>
          <SelectComponent id="carrier" options={optionsCarrier} handler={handler} className={classes.selectCarrier} />
          {products.map((product) => (
            <Grid key={product.id} container>
              <Grid size={6} className={classes.productLabel}>
                {product.name}
              </Grid>
              <Grid size={6}>
                <TextFieldComponent id={`products.${product.id}`} handler={handler} />
              </Grid>
            </Grid>
          ))}
        </>
      ) : (
        <>
          <Grid container spacing={1}>
            <Grid size={6}>
              <TextFieldComponent id="lastname" handler={handlerAddress} />
            </Grid>
            <Grid size={6}>
              <TextFieldComponent id="firstname" handler={handlerAddress} />
            </Grid>
          </Grid>
          <TextFieldComponent id="address1" handler={handlerAddress} />
          <TextFieldComponent id="address2" handler={handlerAddress} />
          <Grid container spacing={1}>
            <Grid size={3}>
              <TextFieldComponent id="zipCode" handler={handlerAddress} />
            </Grid>
            <Grid size={6}>
              <TextFieldComponent id="city" handler={handlerAddress} />
            </Grid>
            <Grid size={3}>
              <TextFieldComponent id="countryCode" handler={handlerAddress} />
            </Grid>
          </Grid>
          <TextFieldComponent id="company" handler={handlerAddress} />
          <TextFieldComponent id="phone" handler={handlerAddress} />
        </>
      )}
    </Modal>
  );
};

const useStyles = makeStyles({
  selectCarrier: {
    marginBottom: '30px',
  },
  productLabel: {
    paddingTop: '10px',
  },
});

export default ReshipmentModal;
